import type { Bounds, Container } from "pixi.js";
import type { Popup } from "popups/Popup";
import { Signal } from "typed-signals";
import { getLocalizedText } from "@softgames/localization";
import { UpdateForcerPopupLayout } from "../../../../../layouts/popups/UpdateForcerPopupLayout";
import type { AppStateController } from '../../../../app/state/AppStateController';
import { soundManager } from '../../../../app/SoundManager';

export interface UpdateForcerPopupConfig {
    readonly state: AppStateController;
    readonly isOptionalUpdate: boolean;
}

export class UpdateForcerPopup implements Popup {
  public readonly onUpdateButtonClicked = new Signal();

  public readonly onCloseButtonClicked = new Signal();

  public readonly root: Container;

  public readonly initialBounds: Bounds;

  private readonly layout: UpdateForcerPopupLayout;

  constructor(config: UpdateForcerPopupConfig) {
    this.layout = new UpdateForcerPopupLayout();
    this.root = this.layout.root;
    this.initialBounds = this.root.getLocalBounds();

    this.init(config);
  }

  private init(config: UpdateForcerPopupConfig): void {
    const { isOptionalUpdate } = config;
    const { updateButton, closeButton, titleText, messageText, updateButtonText } = this.layout;

    if(!config.isOptionalUpdate) {
      closeButton.visible = false;
    }

    updateButton.on('pointertap', () => {
      soundManager.play('UI_Button_Click');
      this.onUpdateButtonClicked.emit();
    });

    closeButton.on('pointertap', () => {
      soundManager.play('UI_Button_Click');
      this.onCloseButtonClicked.emit();
    });
    const description = isOptionalUpdate ? getLocalizedText('optional update message') : getLocalizedText('force update message');
    titleText.text = getLocalizedText('Update');
    messageText.text = description;
    updateButtonText.text = getLocalizedText('Update Now');

    // Make placeholderBG interactive to block touches
    this.layout.placeholderBG.interactive = true;
    this.layout.placeholderBG.eventMode = 'static';
    // Add empty handler to block touches
    this.layout.placeholderBG.on('pointertap', () => {
      // Block touches, do nothing
    });
  }

  public resize(): void {
    const { placeholderBG } = this.layout;
    const { width, height } = this.root.pixi.renderer;

    // Get the current scale applied to the root by alignPopups
    const scale = this.root.scale.x;

    // Calculate screen dimensions in popup's local coordinate space
    const screenWidthScaled = width / scale;
    const screenHeightScaled = height / scale;
    
    // Resize placeholderBG to fill entire screen (with 10% overflow for safety)
    // Keep original x, y position from layout, just update size
    placeholderBG.width = screenWidthScaled * 1.1;
    placeholderBG.height = screenHeightScaled * 1.1;
  }
}